import { useState, type ReactNode } from "react";
import { Bug, ChevronRight, Loader2 } from "lucide-react";
import {
  FAMILY_ROLE_LABELS,
  FILE_STATUS_LABELS,
  LINK_FOUND_BY_LABELS,
  LINK_KIND_LABELS,
  LINK_STATE_LABELS,
  MADE_BY_LABELS,
  PROCESSING_STEP_LABELS,
  PROCESSING_STEP_OUTCOME_LABELS,
  PROJECT_CHANGE_KIND_LABELS,
  REFERENCE_KIND_LABELS,
  describeStep,
  type AnswerStep,
  type DebugChange,
  type DebugRun,
  type DebugRunSummary,
  type DebugStep,
  type DocumentLink,
  type LlmRunPicture,
} from "@gb/contracts";
import { formatDayOf } from "@/lib/format";
import { cn } from "@/lib/utils";
import { LinkSentence } from "@/features/brain/document-links";
import type { CameFrom } from "@/features/brain/file-link";
import { useDebugDocument, useDebugImport, useDebugRun, useDebugSteps, useDebugThread } from "./debug-api";
import { useDebugMode } from "./debug-mode";

/**
 * The machinery beside the thing it belongs to (`architecture/0029`): shown
 * only while the switch is on, to an account the server says may debug.
 *
 * **What is shown is what was recorded, as it was recorded.** The owner has
 * not decided what is worth keeping, so nothing here is curated: the card as
 * stored, every link in any state, each step with its internal detail, each
 * change with its raw rows, and each model call with its prompt, output,
 * thinking and tokens. Costs are tokens: nothing in the codebase prices a call.
 *
 * Long bodies sit in collapsed blocks, and a call's bodies are fetched only
 * when a person opens it.
 */

// ------------------------------------------------------------------
// One file
// ------------------------------------------------------------------

export function DocumentDebug({
  projectId,
  documentId,
  from,
}: {
  projectId: string;
  documentId: string;
  from?: CameFrom;
}) {
  const { on } = useDebugMode();
  const data = useDebugDocument(projectId, documentId, on);
  if (!on) return null;
  return (
    <DebugFrame title="How the system handled this file">
      <Loading query={data}>
        {(debug) => (
          <>
            <Block title="The card, as stored" open>
              <Facts
                rows={[
                  ["Title", debug.card.title],
                  ["Type", debug.card.docType],
                  ["Language", debug.card.language],
                  ["Date it states", debug.card.documentDate],
                  ["Revision", debug.card.revision],
                  ["How to read it", debug.card.howToRead],
                  ["Summary", debug.card.summary],
                  // What the file names, not who is involved: the brief decides
                  // that, from files about the project (2026-09-25, `document-ai/0015`).
                  ["Organisations it names", list(debug.card.parties)],
                  // Who signed or stamped it, and where its site is (2026-09-26).
                  // The empty lines the card read too, since the lighter
                  // approach of 2026-09-26 (CON-06). A line it did not read is
                  // in neither row.
                  ["Signed or stamped, as the card read it", list(signatureLines(debug.card.signatures, true))],
                  ["Signature lines empty, as the card read them", list(signatureLines(debug.card.signatures, false))],
                  ["Project it names", debug.card.aboutProject],
                  ["Site", debug.card.site],
                  ["Site country", debug.card.siteCountry],
                  ["Other project names in it", list(debug.card.projectNames)],
                  ["Card written by", madeBy(debug.card.madeBy)],
                  ["Carded", when(debug.card.cardedAt)],
                  ["Folder", debug.file.sourcePath],
                  ["SHA-256", debug.file.checksumSha256],
                  ["Unpacked from", debug.file.parentDocumentId],
                  ["Arrived in import", debug.file.importId],
                ]}
              />
            </Block>

            <Block title={`Reference numbers (${debug.references.length})`}>
              {debug.references.length === 0 ? (
                <None>The card read no reference numbers.</None>
              ) : (
                <ul className="flex flex-col gap-0.5">
                  {debug.references.map((reference, index) => (
                    <li key={index} className="font-mono text-[0.78rem] text-ink">
                      {reference.value}{" "}
                      <span className="text-muted">
                        · {reference.own ? "its own" : "quoted"} ·{" "}
                        {REFERENCE_KIND_LABELS[reference.kind as keyof typeof REFERENCE_KIND_LABELS] ?? reference.kind} ·
                        joined as {reference.normalised}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Block>

            <Block title="Why it sits where it does" open>
              <Facts
                rows={[
                  // The tenant's categories, keys as stored (`document-ai/0032`):
                  // a key the list no longer has shows here as it is, and the
                  // file is "Not sorted yet" everywhere else.
                  ["Main category", debug.card.mainCategory ?? "None: not sorted yet"],
                  ["Main category set by", madeBy(debug.card.categoryMadeBy)],
                  ["Other categories", list(debug.card.otherCategories)],
                  [
                    "Version",
                    `${FAMILY_ROLE_LABELS[debug.placement.family.role as keyof typeof FAMILY_ROLE_LABELS] ?? debug.placement.family.role}${
                      debug.placement.family.related ? ` — defers to “${debug.placement.family.related.name}”` : ""
                    }${debug.placement.family.name ? ` — family “${debug.placement.family.name}”` : ""}`,
                  ],
                  ["Placed by", madeBy(debug.placement.family.madeBy)],
                  ["Placement reason", debug.placement.family.reason],
                  ["Placed", when(debug.placement.family.placedAt)],
                  [
                    "Question waiting",
                    debug.placement.family.question
                      ? `${debug.placement.family.question.relation}: ${debug.placement.family.question.reason}`
                      : null,
                  ],
                  [
                    "Status",
                    debug.placement.status.status
                      ? (FILE_STATUS_LABELS[debug.placement.status.status as keyof typeof FILE_STATUS_LABELS] ??
                        debug.placement.status.status)
                      : debug.placement.status.checkedAt
                        ? "Checked: no claim"
                        : "Not checked yet",
                  ],
                  ["Status set by", madeBy(debug.placement.status.madeBy)],
                  ["Status reason", debug.placement.status.reason],
                  [
                    // The check's question, as stored (2026-09-26): what it
                    // would have hidden the file as, and each reason it asked.
                    "Hide asked",
                    debug.placement.status.question
                      ? `${debug.placement.status.question.status}: ${debug.placement.status.question.doubts
                          .map((doubt) => (doubt.value ? `${doubt.kind} “${doubt.value}”` : doubt.kind))
                          .join("; ")}`
                      : null,
                  ],
                  ["Checked", when(debug.placement.status.checkedAt)],
                  ["Read for links", when(debug.placement.linkedAt) ?? "Not yet"],
                ]}
              />
            </Block>

            <Block title={`Links, in every state (${debug.links.length})`} open>
              <Links projectId={projectId} links={debug.links} from={from} />
            </Block>

            <Block title={`Steps (${debug.steps.length})`}>
              <Steps steps={debug.steps} />
            </Block>

            <Block title={`Change log (${debug.changes.length})`}>
              <Changes changes={debug.changes} />
            </Block>

            <FileRuns projectId={projectId} runs={debug.runs} />
          </>
        )}
      </Loading>
    </DebugFrame>
  );
}

/**
 * A file's model calls, its own apart from its import's (2026-09-25,
 * `architecture/0029`). The heading counted both as the file's: 19 German
 * files with no call of their own read "Model calls (5)", five calls over the
 * whole import. The server lists its own first.
 */
function FileRuns({ projectId, runs }: { projectId: string; runs: DebugRunSummary[] }) {
  const own = runs.filter((run) => run.subject?.kind === "project_document");
  const imports = runs.filter((run) => run.subject?.kind !== "project_document");
  const importCount = new Set(imports.map((run) => run.subject?.id ?? "")).size;
  return (
    <Block title={modelCallsTitle(own.length, imports.length, importCount)} open>
      {runs.length === 0 ? (
        <None>No model call was recorded.</None>
      ) : (
        <div className="flex flex-col gap-2">
          <div>
            <p className="mb-1 text-[0.76rem] font-semibold text-ink-slate">For this file</p>
            {own.length === 0 ? <None>None. This file was read without a model call of its own.</None> : <RunList projectId={projectId} runs={own} />}
          </div>
          {imports.length > 0 && (
            <div>
              <p className="mb-1 text-[0.76rem] font-semibold text-ink-slate">
                For {importCount === 1 ? "its import" : "its imports"}, over every file in {importCount === 1 ? "it" : "them"}
              </p>
              <RunList projectId={projectId} runs={imports} />
            </div>
          )}
        </div>
      )}
    </Block>
  );
}

/** "Model calls (1 for this file, 5 for its import)". */
function modelCallsTitle(own: number, imports: number, importCount: number): string {
  const mine = own === 0 ? "none for this file" : `${own} for this file`;
  if (imports === 0) return `Model calls (${mine})`;
  return `Model calls (${mine}, ${imports} for ${importCount === 1 ? "its import" : "its imports"})`;
}

// ------------------------------------------------------------------
// One import
// ------------------------------------------------------------------

export function ImportDebug({ projectId, importId }: { projectId: string; importId: string }) {
  const { on } = useDebugMode();
  const data = useDebugImport(projectId, importId, on);
  if (!on) return null;
  return (
    <DebugFrame title="How the system organised this import">
      <Loading query={data}>
        {(debug) => (
          <>
            <Block title={`Steps over the whole import (${debug.steps.length})`} open>
              <Steps steps={debug.steps} />
            </Block>
            <Block title={`Model calls (${debug.runs.length})`} open>
              <RunList projectId={projectId} runs={debug.runs} />
            </Block>
          </>
        )}
      </Loading>
    </DebugFrame>
  );
}

// ------------------------------------------------------------------
// One answer
// ------------------------------------------------------------------

/**
 * An answer, as it was made (Phase 5, 2026-09-26; §3 of the design): the
 * answering agent's steps and its model calls in one list, in the order they
 * happened. Each call sits after the step it served: a model turn after its
 * turn row, a look after its look, the review after the review row. The
 * backoffice shows the same list, with each step's input and output whole.
 *
 * The steps are read through the tenant's `listSteps`, whose rows carry no
 * run id, so a call is placed by time: after the last step that had started
 * when the call was recorded. An answer of the old path, or a scripted demo
 * answer, has no steps, and shows its calls alone. The first design's open
 * block for the source selector went with the selector (slice X).
 */
export function ThreadDebug({ projectId, threadId }: { projectId: string; threadId: string }) {
  const { on } = useDebugMode();
  const data = useDebugThread(projectId, threadId, on);
  const steps = useDebugSteps(projectId, threadId, on);
  if (!on) return null;
  return (
    <DebugFrame title="How this answer was made">
      <Loading query={data}>
        {(debug) => (
          <>
            {steps.isError && (
              <p role="alert" className="text-[0.8rem] font-semibold text-danger">
                The steps could not be read: {steps.error.message}
              </p>
            )}
            {debug.runs.length === 0 && (steps.data ?? []).length === 0 ? (
              <None>No model call and no step was recorded for this answer. A scripted demo answer makes none.</None>
            ) : (
              <ol className="flex flex-col gap-2">
                {interleave(steps.data ?? [], debug.runs).map((item) =>
                  item.kind === "step" ? (
                    <li key={`step-${item.step.seq}`}>
                      <StepItem step={item.step} />
                    </li>
                  ) : (
                    <li key={`run-${item.run.id}`}>
                      <Block title={runTitle(item.run)}>
                        <RunBody run={item.run} />
                      </Block>
                    </li>
                  ),
                )}
              </ol>
            )}
          </>
        )}
      </Loading>
    </DebugFrame>
  );
}

type ThreadItem = { kind: "step"; step: AnswerStep } | { kind: "run"; run: DebugRun };

/**
 * The steps in order of `seq`, each call after the last step that had
 * started when the call was recorded; calls recorded before any step first.
 * Every step and every call is in the list once: nothing is dropped when the
 * clocks disagree, only placed a row early or late.
 */
export function interleave(steps: readonly AnswerStep[], runs: readonly DebugRun[]): ThreadItem[] {
  const ordered = [...steps].sort((a, b) => a.seq - b.seq);
  const after = new Map<number, DebugRun[]>();
  const first: DebugRun[] = [];
  for (const run of [...runs].sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt))) {
    const at = Date.parse(run.createdAt);
    let index = -1;
    ordered.forEach((step, each) => {
      if (Date.parse(step.startedAt) <= at) index = each;
    });
    if (index < 0) first.push(run);
    else after.set(index, [...(after.get(index) ?? []), run]);
  }
  return [
    ...first.map((run): ThreadItem => ({ kind: "run", run })),
    ...ordered.flatMap((step, index): ThreadItem[] => [
      { kind: "step", step },
      ...(after.get(index) ?? []).map((run): ThreadItem => ({ kind: "run", run })),
    ]),
  ];
}

/** One step: its line in the reader's locale, how it ended, why, and its detail as recorded. */
function StepItem({ step }: { step: AnswerStep }) {
  const took = step.finishedAt ? Math.max(0, Date.parse(step.finishedAt) - Date.parse(step.startedAt)) : null;
  return (
    <div className="rounded-card border border-border bg-surface px-3 py-2 text-[0.8rem]">
      <p className="text-ink">
        <span className="font-mono text-[0.72rem] text-muted">{step.seq}.</span> {describeStep(step, undefined) || "(an empty note)"}
      </p>
      <p className="text-muted">
        {step.kind}
        {step.tool ? ` · ${step.tool}` : ""} · {step.outcome} · turn {step.turn}
        {took !== null ? ` · ${took} ms` : " · running"}
        {step.sources.length > 0 ? ` · cited as ${step.sources.map((n) => `[${n}]`).join(" ")}` : ""}
      </p>
      {step.purpose && <p className="text-ink-slate">Why: {step.purpose}</p>}
      {step.failure && <p className="text-danger">{step.failure}</p>}
      {step.note && (
        <p className="text-ink-slate">
          Note from a {step.note.form} block{step.note.markersRemoved > 0 ? `, ${step.note.markersRemoved} markers removed` : ""}
          {step.note.shown ? "" : ", not shown to the reader"}
        </p>
      )}
      {step.detail !== null && step.detail !== undefined && <Json value={step.detail} label="Detail" />}
    </div>
  );
}

// ------------------------------------------------------------------
// Pieces
// ------------------------------------------------------------------

function DebugFrame({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section
      aria-label={title}
      className="mt-5 rounded-card-lg border-[1.5px] border-dashed border-warn/60 bg-surface px-4 py-3"
    >
      <p className="flex items-center gap-1.5 font-display text-[0.66rem] font-semibold tracking-[0.12em] text-warn-text uppercase">
        <Bug size={13} aria-hidden />
        Debug · {title}
      </p>
      <p className="mt-0.5 text-[0.76rem] text-muted">
        Shown only to accounts with debug access. Tokens are as the provider counted them; nothing here is priced.
      </p>
      <div className="mt-2 flex flex-col gap-2">{children}</div>
    </section>
  );
}

function Loading<T>({
  query,
  children,
}: {
  query: { isPending: boolean; isError: boolean; error: Error | null; data: T | undefined };
  children: (data: T) => ReactNode;
}) {
  if (query.isPending) {
    return (
      <p className="flex items-center gap-2 text-[0.82rem] text-muted" role="status">
        <Loader2 size={14} className="animate-spin" /> Reading the record…
      </p>
    );
  }
  if (query.isError || query.data === undefined) {
    return (
      <p role="alert" className="text-[0.82rem] font-semibold text-danger">
        {query.error?.message ?? "Couldn’t read the record."}
      </p>
    );
  }
  return <>{children(query.data)}</>;
}

function Block({ title, open = false, children }: { title: string; open?: boolean; children: ReactNode }) {
  return (
    <details open={open} className="group rounded-card border border-border bg-surface-sunken px-3 py-2">
      <summary className="flex cursor-pointer list-none items-center gap-1.5 text-[0.8rem] font-semibold text-ink">
        <ChevronRight size={13} className="transition-transform group-open:rotate-90" aria-hidden />
        {title}
      </summary>
      <div className="mt-2">{children}</div>
    </details>
  );
}

function None({ children }: { children: ReactNode }) {
  return <p className="text-[0.8rem] text-muted">{children}</p>;
}

function Facts({ rows }: { rows: [string, string | null | undefined][] }) {
  const shown = rows.filter(([, value]) => value !== null && value !== undefined && value !== "");
  if (shown.length === 0) return <None>Nothing recorded.</None>;
  return (
    <dl className="grid grid-cols-[minmax(0,11rem)_minmax(0,1fr)] gap-x-3 gap-y-1 text-[0.8rem]">
      {shown.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="text-muted">{label}</dt>
          <dd className="break-words text-ink">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function Links({ projectId, links, from }: { projectId: string; links: DocumentLink[]; from?: CameFrom }) {
  if (links.length === 0) return <None>No link to or from this file, in any state.</None>;
  return (
    <ul className="flex flex-col gap-2">
      {links.map((link, index) => (
        <li key={link.id ?? index} className={cn("text-[0.8rem]", link.state === "removed" && "opacity-70")}>
          <p className="text-ink">
            <span className="font-semibold">{LINK_STATE_LABELS[link.state]}:</span>{" "}
            <LinkSentence projectId={projectId} link={link} from={from} />
          </p>
          <p className="text-muted">
            {LINK_KIND_LABELS[link.kind]} · found by: {LINK_FOUND_BY_LABELS[link.foundBy]} · made by:{" "}
            {MADE_BY_LABELS[link.madeBy]}
            {link.createdAt ? ` · ${formatDayOf(link.createdAt)}` : ""}
            {link.importId ? ` · import ${link.importId.slice(0, 8)}` : ""}
          </p>
          {link.reason && <p className="text-ink-slate">{link.reason}</p>}
          {link.evidence?.numbers && (
            <p className="font-mono text-[0.76rem] text-ink-slate">
              {link.evidence.numbers.map((number) => `“${number.quoted}” = “${number.owned}”`).join("; ")}
            </p>
          )}
          {link.evidence?.messageId && (
            <p className="font-mono text-[0.76rem] text-ink-slate">In-Reply-To: {link.evidence.messageId}</p>
          )}
          {link.removed && (
            <p className="text-muted">
              Removed {formatDayOf(link.removed.at)} · {MADE_BY_LABELS[link.removed.madeBy]}
              {link.removed.reason ? ` · ${link.removed.reason}` : ""}
            </p>
          )}
        </li>
      ))}
    </ul>
  );
}

function Steps({ steps }: { steps: DebugStep[] }) {
  if (steps.length === 0) return <None>No step was recorded.</None>;
  return (
    <ul className="flex flex-col gap-1.5">
      {steps.map((step) => (
        <li key={step.id} className="text-[0.8rem]">
          <p className="text-ink">
            <span className="font-semibold">
              {PROCESSING_STEP_LABELS[step.step as keyof typeof PROCESSING_STEP_LABELS] ?? step.step}
            </span>{" "}
            <span className="text-muted">
              · {PROCESSING_STEP_OUTCOME_LABELS[step.outcome as keyof typeof PROCESSING_STEP_OUTCOME_LABELS] ?? step.outcome}{" "}
              · attempt {step.attempt} · {formatDayOf(step.startedAt)} ·{" "}
              {Math.max(0, new Date(step.finishedAt).getTime() - new Date(step.startedAt).getTime())} ms
            </span>
          </p>
          {step.reason && <p className="text-ink-slate">{step.reason}</p>}
          <NotRead detail={step.detail} />
          {step.detail && <Json value={step.detail} label="Detail" />}
        </li>
      ))}
    </ul>
  );
}

/**
 * The pictures a `describe` step did not read, and why (`architecture/0030`):
 * the question a failed reading raises is "which one", and the detail's JSON
 * answers it only to somebody who reads JSON.
 *
 * Since 2026-09-26 (CON-08) every picture not read is listed, in runs by page
 * or by sheet: `pictures` in a run, on `fromPage` to `toPage`, or on `sheet`.
 * A record written before then lists one picture an entry, by `sequence`, and
 * reads as it did. The files a reading was copied from follow (EFF-02).
 */
function NotRead({ detail }: { detail: Record<string, unknown> | null }) {
  const notRead = Array.isArray(detail?.notRead) ? (detail.notRead as NotReadEntry[]) : [];
  const copiedFrom = Array.isArray(detail?.copiedFrom)
    ? (detail.copiedFrom as { filename?: unknown; pictures?: unknown }[])
    : [];
  if (notRead.length === 0 && copiedFrom.length === 0) return null;
  return (
    <div className="mt-0.5">
      {notRead.length > 0 && (
        <>
          <p className="text-ink-slate">Pictures not read:</p>
          <ul className="ml-3 list-disc text-ink-slate">
            {notRead.map((entry, index) => (
              <li key={index}>
                {placeOf(entry)} · {NOT_READ_WORDS[String(entry.why)] ?? String(entry.why)}
                {typeof entry.detail === "string" && entry.detail ? ` ${entry.detail}` : ""}
              </li>
            ))}
          </ul>
        </>
      )}
      {copiedFrom.length > 0 && (
        <>
          <p className="text-ink-slate">Readings copied from files with the same pictures:</p>
          <ul className="ml-3 list-disc text-ink-slate">
            {copiedFrom.map((entry, index) => (
              <li key={index}>
                {String(entry.filename ?? "") || "a file of this project"} · {Number(entry.pictures)}{" "}
                {Number(entry.pictures) === 1 ? "picture" : "pictures"}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/** One entry of `notRead`, in any shape a record has held. */
interface NotReadEntry {
  why?: unknown;
  detail?: unknown;
  sequence?: unknown;
  pictures?: unknown;
  fromPage?: unknown;
  toPage?: unknown;
  sheet?: unknown;
}

/** Where the pictures of one entry are, and how many. */
function placeOf(entry: NotReadEntry): string {
  const pages = () =>
    entry.fromPage === entry.toPage ? `Page ${String(entry.fromPage)}` : `Pages ${String(entry.fromPage)} to ${String(entry.toPage)}`;
  // A run of scanned pages with no render, or a record from before
  // 2026-09-26: one picture, by its sequence.
  if (typeof entry.pictures !== "number") {
    return typeof entry.fromPage === "number" ? pages() : `Picture at sequence ${String(entry.sequence)}`;
  }
  const count = `${entry.pictures} ${entry.pictures === 1 ? "picture" : "pictures"}`;
  if (typeof entry.sheet === "string") return `Sheet “${entry.sheet}” · ${count}`;
  if (typeof entry.fromPage === "number") return `${pages()} · ${count}`;
  return `No page · ${count}`;
}

/** `NotReadWhy` in words. A reason recorded by a later version shows as written. */
const NOT_READ_WORDS: Record<string, string> = {
  failed: "the call gave no usable answer (its run record says why)",
  unsupported: "a type no model takes",
  pastBudget: "past the allowance of model calls for one file",
  noModel: "no vision model is configured",
  bytesMissing: "missing from storage",
  notStored: "read, but what it says could not be stored",
  notRendered: "a scanned page that could not be rendered, so there is no picture to read.",
};

function Changes({ changes }: { changes: DebugChange[] }) {
  if (changes.length === 0) return <None>No change was recorded.</None>;
  return (
    <ul className="flex flex-col gap-1.5">
      {changes.map((change) => (
        <li key={change.id} className="text-[0.8rem]">
          <p className="text-ink">{change.summary}</p>
          <p className="text-muted">
            {PROJECT_CHANGE_KIND_LABELS[change.kind as keyof typeof PROJECT_CHANGE_KIND_LABELS] ?? change.kind} ·{" "}
            {MADE_BY_LABELS[change.madeBy]} · {formatDayOf(change.createdAt)}
            {change.undoesId ? " · an undo" : ""}
          </p>
          {change.before && <Json value={change.before} label="Before" />}
          {change.after && <Json value={change.after} label="After" />}
        </li>
      ))}
    </ul>
  );
}

function RunList({ projectId, runs }: { projectId: string; runs: DebugRunSummary[] }) {
  if (runs.length === 0) return <None>No model call was recorded.</None>;
  return (
    <ul className="flex flex-col gap-1.5">
      {runs.map((run) => (
        <RunRow key={run.id} projectId={projectId} run={run} />
      ))}
    </ul>
  );
}

function RunRow({ projectId, run }: { projectId: string; run: DebugRunSummary }) {
  const [open, setOpen] = useState(false);
  const whole = useDebugRun(projectId, run.id, open);
  return (
    <li className="text-[0.8rem]">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="flex w-full cursor-pointer items-start gap-1.5 text-left"
      >
        <ChevronRight size={13} className={cn("mt-0.5 shrink-0 transition-transform", open && "rotate-90")} aria-hidden />
        <span className="min-w-0">
          <span className="font-semibold text-ink">{runTitle(run)}</span>{" "}
          <span className="text-muted">
            · {run.status}
            {run.status !== "ok" && run.errorClass ? ` · ${run.errorClass}` : ""} · {run.model} · {tokens(run)} ·{" "}
            {run.latencyMs} ms · {formatDayOf(run.createdAt)}
            {run.hasThinking ? " · thought" : ""}
          </span>
        </span>
      </button>
      {open && (
        <div className="mt-1.5 ml-4">
          <Loading query={whole}>{(detail) => <RunBody run={detail} />}</Loading>
        </div>
      )}
    </li>
  );
}

function RunBody({ run }: { run: DebugRun }) {
  return (
    <div className="flex flex-col gap-1.5 text-[0.8rem]">
      <p className="text-muted">
        {run.provider} · {run.model} · prompt version {run.promptVersion}
        {run.promptScope ? ` (${run.promptScope})` : " (code default)"} · {run.status} · {tokens(run)} ·{" "}
        {run.latencyMs} ms{run.imageCount !== null ? ` · ${counted(run.imageCount, "image")}` : ""}
        {run.errorClass ? ` · ${run.errorClass}` : ""}
      </p>
      {run.errorMessage && (
        <div className="rounded-control border border-danger/40 bg-surface px-2 py-1">
          <p className="text-[0.76rem] font-semibold text-danger">Provider’s message</p>
          <p className="mt-0.5 font-mono text-[0.74rem] break-words whitespace-pre-wrap text-ink">{run.errorMessage}</p>
        </div>
      )}
      {run.images && run.images.length > 0 && (
        <div>
          <p className="text-[0.76rem] font-semibold text-ink-slate">Pictures, as stored and as sent</p>
          <ul className="ml-3 list-disc font-mono text-[0.74rem] text-ink">
            {run.images.map((image, index) => (
              <li key={index}>
                {picture(image.before)}
                {image.after ? ` → ${picture(image.after)} (${IMAGE_ACTIONS[image.action] ?? image.action})` : " · not sent"}
              </li>
            ))}
          </ul>
        </div>
      )}
      {run.systemText && <Text label="System prompt, as sent" value={run.systemText} />}
      <Text label="Prompt, as sent" value={run.promptText ?? "(not recorded)"} />
      <Text label="Thinking" value={run.thinking ?? "(the model returned none)"} />
      {run.output ? <Json value={run.output} label="Output" open /> : <p className="text-muted">No output.</p>}
    </div>
  );
}

function Text({ label, value }: { label: string; value: string }) {
  return (
    <details className="rounded-control border border-border bg-surface px-2 py-1">
      <summary className="cursor-pointer text-[0.76rem] font-semibold text-ink-slate">
        {label} <span className="font-normal text-muted">({value.length.toLocaleString("en-GB")} characters)</span>
      </summary>
      <pre className="mt-1 max-h-[28rem] overflow-auto text-[0.74rem] leading-[1.45] whitespace-pre-wrap text-ink">
        {value}
      </pre>
    </details>
  );
}

function Json({ value, label, open = false }: { value: unknown; label: string; open?: boolean }) {
  return (
    <details open={open} className="mt-0.5 rounded-control border border-border bg-surface px-2 py-1">
      <summary className="cursor-pointer text-[0.76rem] font-semibold text-ink-slate">{label}</summary>
      <pre className="mt-1 max-h-[24rem] overflow-auto font-mono text-[0.72rem] leading-[1.4] whitespace-pre-wrap text-ink">
        {JSON.stringify(value, null, 2)}
      </pre>
    </details>
  );
}

function runTitle(run: Pick<DebugRunSummary, "touchpoint">): string {
  return RUN_NAMES[run.touchpoint] ?? run.touchpoint;
}

/** What each call is for, as a heading. The slug is still shown beside it. */
const RUN_NAMES: Record<string, string> = {
  "documents.card": "The card (documents.card)",
  "documents.read-page": "Reading a page from its image (documents.read-page)",
  "documents.page-gate": "The figure gate (documents.page-gate)",
  "documents.transcribe-page": "Transcribing a page with no text layer (documents.transcribe-page)",
  "vision.transcribe-page": "Reading a page image (vision.transcribe-page)",
  "vision.describe-image": "Describing a picture (vision.describe-image)",
  "projects.project-check": "The project check (projects.project-check)",
  "projects.party-groups": "Company names grouped for the check (projects.party-groups)",
  "projects.brief": "The brief (projects.brief)",
  "projects.families": "Families (projects.families)",
  "projects.links": "Links by reading (projects.links)",
  "projects.related-files": "Choosing the files to compare (projects.related-files)",
  "chat.question-language": "Naming the question's language (chat.question-language)",
  "chat.answer": "A turn of the answer (chat.answer)",
  "chat.look": "A look at a region (chat.look)",
  "chat.review-answer": "The confidence review (chat.review-answer)",
};

function tokens(run: Pick<DebugRunSummary, "inputTokens" | "outputTokens" | "cacheReadTokens" | "cacheWriteTokens">): string {
  const parts = [`${run.inputTokens ?? "?"} in`, `${run.outputTokens ?? "?"} out`];
  if (run.cacheReadTokens) parts.push(`${run.cacheReadTokens} from cache`);
  if (run.cacheWriteTokens) parts.push(`${run.cacheWriteTokens} to cache`);
  return `${parts.join(", ")} tokens`;
}

/** "1 image", "0 images", "3 images". */
function counted(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

/** "image/jpeg 8116 × 3185 px, 578 KB". */
function picture(value: LlmRunPicture): string {
  const size = value.widthPx && value.heightPx ? ` ${value.widthPx} × ${value.heightPx} px` : "";
  return `${value.mimeType}${size}, ${Math.max(1, Math.round(value.bytes / 1024)).toLocaleString("en-GB")} KB`;
}

const IMAGE_ACTIONS: Record<string, string> = {
  unchanged: "sent as stored",
  "re-encoded": "re-encoded",
  resized: "shrunk to fit",
};

function list(values: string[] | null): string | null {
  return values && values.length > 0 ? values.join(", ") : null;
}

/** The organisations of the signature lines the card read as signed, or as empty. */
function signatureLines(lines: { organisation: string; signed: boolean }[] | null, signed: boolean): string[] | null {
  return lines ? lines.filter((line) => line.signed === signed).map((line) => line.organisation) : null;
}

function madeBy(value: string | null): string | null {
  return value ? (MADE_BY_LABELS[value as keyof typeof MADE_BY_LABELS] ?? value) : null;
}

function when(value: string | null): string | null {
  return value ? formatDayOf(value) : null;
}
